import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  useMediaQuery,
} from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";

interface ConfirmServiceDialogProps {
  open: boolean;
  offerNumber: string;
  defaultArrivalDate?: string;
  onConfirm: (data: {
    arrivalDate: string;
    arrivalTimeFrom: string;
    arrivalTimeTo: string;
    message?: string;
  }) => void;
  onClose: () => void;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45];

const pad = (n: number): string => n.toString().padStart(2, "0");

/* ── Green accent tokens (success action) ── */
const green = "#27AE60";
const greenDark = "#219A52";
const greenSoft = "rgba(39, 174, 96, 0.06)";

const ConfirmServiceDialog = ({
  open,
  offerNumber,
  defaultArrivalDate,
  onConfirm,
  onClose,
}: ConfirmServiceDialogProps) => {
  useRedesignSetup();
  const { translate } = useTranslation(["serviceOffers", "common"]);
  const isPhone = useMediaQuery("(max-width:600px)");

  const [arrivalDate, setArrivalDate] = useState("");
  const [fromHour, setFromHour] = useState<number>(9);
  const [fromMinute, setFromMinute] = useState<number>(0);
  const [toHour, setToHour] = useState<number>(10);
  const [toMinute, setToMinute] = useState<number>(0);
  const [message, setMessage] = useState("");

  // Inject dialog-specific keyframes
  useEffect(() => {
    const styleId = "confirm-service-dialog-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes dialogSlideUp {
          0% { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes successPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(39,174,96,0.15); }
          50% { box-shadow: 0 0 0 8px rgba(39,174,96,0); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    if (open) {
      if (defaultArrivalDate) {
        const dateStr = defaultArrivalDate.substring(0, 10);
        setArrivalDate(dateStr);
      }
      // Pre-fill sensible time defaults
      setFromHour(9);
      setFromMinute(0);
      setToHour(10);
      setToMinute(0);
      setMessage("");
    }
  }, [open, defaultArrivalDate]);

  const handleConfirm = () => {
    const arrivalTimeFrom = `${pad(fromHour)}:${pad(fromMinute)}`;
    const arrivalTimeTo = `${pad(toHour)}:${pad(toMinute)}`;
    onConfirm({
      arrivalDate,
      arrivalTimeFrom,
      arrivalTimeTo,
      message: message.trim() || undefined,
    });
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setArrivalDate("");
    setFromHour(9);
    setFromMinute(0);
    setToHour(10);
    setToMinute(0);
    setMessage("");
  };

  const isValid = arrivalDate.length > 0;

  /* ── Shared MUI Select sx ── */
  const timeSelectSx = {
    flex: 1,
    fontFamily: dt.fontMono,
    fontSize: "14px",
    fontWeight: 500,
    textAlign: "center" as const,
    backgroundColor: dt.inputBg,
    borderRadius: dt.radiusSm,
    height: "44px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: dt.cardBorder,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: dt.blue,
      borderWidth: "1.5px",
    },
    "& .MuiSelect-select": {
      textAlign: "center",
    },
  };

  const timeMenuProps = {
    PaperProps: {
      sx: {
        maxHeight: 200,
        borderRadius: dt.radiusSm,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      },
    },
  };

  const timeMenuItemSx = {
    fontFamily: dt.fontMono,
    fontSize: "14px",
    py: 0.5,
    justifyContent: "center",
  };

  /* ── Render a time select pair (hour : minute) ── */
  const renderTimeSelect = (
    hourVal: number,
    minuteVal: number,
    onHourChange: (v: number) => void,
    onMinuteChange: (v: number) => void
  ) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Select
        value={hourVal}
        onChange={(e) => onHourChange(Number(e.target.value))}
        size="small"
        MenuProps={timeMenuProps}
        sx={timeSelectSx}
      >
        {HOURS.map((h) => (
          <MenuItem key={h} value={h} sx={timeMenuItemSx}>
            {pad(h)}
          </MenuItem>
        ))}
      </Select>
      <Box
        sx={{
          fontFamily: dt.fontMono,
          fontSize: "16px",
          fontWeight: 700,
          color: dt.textMuted,
          userSelect: "none",
          lineHeight: 1,
        }}
      >
        :
      </Box>
      <Select
        value={minuteVal}
        onChange={(e) => onMinuteChange(Number(e.target.value))}
        size="small"
        MenuProps={timeMenuProps}
        sx={timeSelectSx}
      >
        {MINUTES.map((m) => (
          <MenuItem key={m} value={m} sx={timeMenuItemSx}>
            {pad(m)}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: dt.radiusLg,
          overflow: "hidden",
          fontFamily: dt.font,
          "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
          animation: "dialogSlideUp 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          ...(isPhone && {
            margin: "16px",
            width: "calc(100% - 32px)",
            maxWidth: "none",
          }),
        },
      }}
    >
      {/* ── Header with gradient accent bar ── */}
      <Box
        sx={{
          height: "4px",
          background: `linear-gradient(90deg, ${green} 0%, #2ECC71 50%, ${green}00 100%)`,
        }}
      />
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 0.5,
          pt: 2,
          px: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: greenSoft,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "successPulse 2s ease-in-out infinite",
            }}
          >
            <CheckCircleOutlineIcon
              sx={{ color: green, fontSize: 22 }}
            />
          </Box>
          <Box
            sx={{
              fontWeight: 700,
              fontSize: { xs: "18px", sm: "20px" },
              color: dt.textPrimary,
              letterSpacing: "-0.3px",
            }}
          >
            {translate("serviceOffers:confirmServiceTitle")}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0.5, pb: 0 }}>
        {/* ── Subtitle ── */}
        <Box
          sx={{
            fontSize: "14px",
            color: dt.textSecondary,
            mb: 3,
            mt: 0.5,
            lineHeight: 1.6,
          }}
        >
          {translate("serviceOffers:confirmServiceText", {
            number: offerNumber,
          })}
        </Box>

        {/* ── Arrival Date ── */}
        <Box sx={{ mb: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 1,
            }}
          >
            <CalendarTodayOutlinedIcon
              sx={{ fontSize: 15, color: dt.textMuted }}
            />
            <Box
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: dt.textPrimary,
                textTransform: "uppercase",
                letterSpacing: "0.8px",
              }}
            >
              {translate("serviceOffers:arrivalDate")}
            </Box>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={arrivalDate ? dayjs(arrivalDate) : null}
              onChange={(newVal) =>
                setArrivalDate(newVal ? newVal.format("YYYY-MM-DD") : "")
              }
              format="DD.MM.YYYY"
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                  placeholder: translate("serviceOffers:confirmServiceSelectDatePlaceholder"),
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      fontFamily: dt.fontMono,
                      fontSize: "14px",
                      borderRadius: dt.radiusSm,
                      backgroundColor: dt.inputBg,
                      height: "46px",
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: dt.cardBorder },
                      "&.Mui-focused fieldset": {
                        borderColor: dt.blue,
                        borderWidth: "1.5px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontFamily: dt.font,
                      fontSize: "13px",
                    },
                  },
                },
                popper: {
                  sx: {
                    "& .MuiPaper-root": {
                      borderRadius: dt.radiusSm,
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    },
                    "& .MuiPickersDay-root.Mui-selected": {
                      backgroundColor: green,
                      "&:hover": { backgroundColor: greenDark },
                    },
                    "& .MuiPickersDay-today": {
                      borderColor: green,
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        {/* ── Time Window ── */}
        <Box sx={{ mb: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 1,
            }}
          >
            <ScheduleOutlinedIcon
              sx={{ fontSize: 15, color: dt.textMuted }}
            />
            <Box
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: dt.textPrimary,
                textTransform: "uppercase",
                letterSpacing: "0.8px",
              }}
            >
              {translate("serviceOffers:arrivalTimeFrom")}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: isPhone ? 1.5 : 2.5,
              flexDirection: isPhone ? "column" : "row",
            }}
          >
            {/* From */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: dt.textSecondary,
                  mb: 0.5,
                }}
              >
                {translate("serviceOffers:arrivalTimeFrom")}
              </Box>
              {renderTimeSelect(
                fromHour,
                fromMinute,
                setFromHour,
                setFromMinute
              )}
            </Box>

            {/* Dash separator (desktop/tablet only) */}
            {!isPhone && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  pb: "12px",
                  color: dt.textMuted,
                  fontWeight: 600,
                  fontSize: "18px",
                  userSelect: "none",
                }}
              >
                —
              </Box>
            )}

            {/* To */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                  color: dt.textSecondary,
                  mb: 0.5,
                }}
              >
                {translate("serviceOffers:arrivalTimeTo")}
              </Box>
              {renderTimeSelect(
                toHour,
                toMinute,
                setToHour,
                setToMinute
              )}
            </Box>
          </Box>
        </Box>

        {/* ── Message (optional) ── */}
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: dt.textPrimary,
              mb: 0.75,
            }}
          >
            {translate("serviceOffers:confirmationMessageLabel")}
          </Box>
          <Box
            sx={{
              background: dt.inputBg,
              borderRadius: dt.radiusSm,
              border: "1.5px solid transparent",
              padding: "12px",
              transition: "all 0.2s ease",
              "&:focus-within": {
                border: `1.5px solid ${dt.blue}`,
                boxShadow: `0 0 0 3px ${dt.blueGlow}`,
              },
            }}
          >
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={translate(
                "serviceOffers:confirmationMessagePlaceholder"
              )}
              rows={3}
              style={{
                border: "none",
                outline: "none",
                width: "100%",
                fontSize: "14px",
                fontFamily: 'var(--font-sans)',
                color: "#1a1a2e",
                background: "transparent",
                resize: "vertical",
                minHeight: "72px",
                boxSizing: "border-box",
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1.5,
          gap: 1,
          flexDirection: isPhone ? "column-reverse" : "row",
          "& > :not(:first-of-type)": {
            ml: isPhone ? "0 !important" : undefined,
          },
        }}
      >
        <Button
          onClick={handleClose}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            color: dt.textSecondary,
            borderRadius: dt.radiusSm,
            px: 2.5,
            py: 1,
            transition: "all 0.2s ease",
            width: isPhone ? "100%" : "auto",
            "&:hover": {
              backgroundColor: dt.inputBg,
              color: dt.textPrimary,
            },
          }}
        >
          {translate("common:buttons.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!isValid}
          startIcon={
            <CheckCircleOutlineIcon
              sx={{ fontSize: "18px !important" }}
            />
          }
          sx={{
            background: `linear-gradient(135deg, ${green} 0%, #2ECC71 100%)`,
            borderRadius: dt.radiusPill,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            px: 3,
            py: 1,
            boxShadow: "0 2px 8px rgba(39,174,96,0.25)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            width: isPhone ? "100%" : "auto",
            whiteSpace: "nowrap",
            "&:hover": {
              background: `linear-gradient(135deg, #2ECC71 0%, ${green} 100%)`,
              transform: "translateY(-2px)",
              boxShadow: "0 6px 20px rgba(39,174,96,0.3)",
            },
            "&.Mui-disabled": {
              background: "#D1D5DB",
              color: "#9CA3AF",
              boxShadow: "none",
            },
          }}
        >
          {translate("serviceOffers:confirmAndSchedule")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmServiceDialog;
